import { Link } from 'react-router-dom';

export interface HeroProps {
  eyebrow?: string;
  headline: string;
  subheadline: string;
  ctaText: string;
  ctaLink: string;
  secondaryCta?: string;
  secondaryCtaLink?: string;
  bgImage: string;
}

const STATS = [
  { value: '70%+', label: 'Energy Cost Reduction', icon: 'ri-flashlight-flash-line' },
  { value: '10+', label: 'Application Types Served', icon: 'ri-building-2-line' },
  { value: 'FL', label: 'Licensed — Tampa Bay Based', icon: 'ri-map-pin-2-line' },
];

export default function HeroSection({
  eyebrow,
  headline,
  subheadline,
  ctaText,
  ctaLink,
  secondaryCta,
  secondaryCtaLink,
  bgImage,
}: HeroProps) {
  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={bgImage}
          alt="Southeast Lighting & Design — commercial project"
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
        {/* Multi-layer cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/97 via-charcoal/78 to-charcoal/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/95 via-transparent to-charcoal/50" />
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at 20% 55%, transparent 30%, rgba(14,14,14,0.6) 100%)',
          }}
        />
      </div>

      {/* Left gold accent strip */}
      <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-transparent via-gold/60 to-transparent opacity-0 animate-hero-fade-in" />

      {/* Right-side architectural grid decoration */}
      <div
        className="hidden xl:block absolute right-0 top-0 h-full w-[340px] opacity-0 animate-hero-fade-in pointer-events-none select-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(201,168,76,0.055) 39px, rgba(201,168,76,0.055) 40px), repeating-linear-gradient(0deg, transparent, transparent 79px, rgba(201,168,76,0.04) 79px, rgba(201,168,76,0.04) 80px)',
        }}
      />
      {/* Right fade-out so grid fades into image */}
      <div className="hidden xl:block absolute right-0 top-0 h-full w-[340px] bg-gradient-to-l from-transparent to-charcoal/0 pointer-events-none" />

      {/* Main content */}
      <div className="relative z-10 w-full mx-auto px-6 lg:px-12 xl:px-20 pt-40 pb-36 lg:pb-40">
        <div className="max-w-3xl">
          {eyebrow && (
            <div className="opacity-0 animate-hero-fade-up flex items-center gap-4 mb-8">
              <span className="block w-8 h-px bg-gold flex-shrink-0" />
              <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                {eyebrow}
              </span>
            </div>
          )}

          <h1
            className="font-serif text-white opacity-0 animate-hero-fade-up-delay leading-[1.04] mb-8"
            style={{ fontSize: 'clamp(2.6rem, 5.5vw, 5.2rem)', fontWeight: 300 }}
          >
            {formatHeadline(headline)}
          </h1>

          {/* Gold accent rule */}
          <div
            className="h-px bg-gold origin-left scale-x-0 animate-hero-line mb-8"
            style={{ width: '64px' }}
          />

          <p
            className="text-white/58 text-base lg:text-lg leading-[1.95] max-w-lg mb-10 opacity-0 animate-hero-fade-up-delay2"
            style={{ fontWeight: 300 }}
          >
            {subheadline}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-hero-fade-in">
            <Link
              to={ctaLink}
              className="whitespace-nowrap inline-flex items-center justify-center bg-gold text-charcoal text-[10px] font-bold tracking-[0.25em] uppercase px-10 py-4 hover:bg-gold-light transition-all duration-300 cursor-pointer group gap-2.5"
            >
              {ctaText}
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </Link>
            {secondaryCta && secondaryCtaLink && (
              <a
                href={secondaryCtaLink}
                className="whitespace-nowrap inline-flex items-center justify-center border border-white/22 text-white text-[10px] font-semibold tracking-[0.25em] uppercase px-10 py-4 hover:border-gold hover:text-gold transition-all duration-300 cursor-pointer gap-2.5 backdrop-blur-[2px]"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm"></i>
                </div>
                {secondaryCta}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Architectural stats bar — pinned at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-20 opacity-0 animate-hero-fade-in">
        {/* Gold line above bar */}
        <div className="h-px bg-gradient-to-r from-gold/60 via-gold/30 to-transparent mx-6 lg:mx-12 xl:mx-20" />
        <div className="mx-6 lg:mx-12 xl:mx-20">
          <div className="flex flex-col sm:flex-row">
            {STATS.map((stat, i) => (
              <div
                key={stat.label}
                className={`flex items-center gap-5 py-7 px-7 flex-1 bg-charcoal/90 backdrop-blur-sm ${
                  i < STATS.length - 1
                    ? 'border-b sm:border-b-0 sm:border-r border-white/8'
                    : ''
                } ${i === 0 ? 'border-l border-l-gold/40' : ''}`}
              >
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border border-gold/20">
                  <i className={`${stat.icon} text-gold text-base`}></i>
                </div>
                <div>
                  <span
                    className="block font-serif text-gold font-light leading-none mb-1"
                    style={{ fontSize: 'clamp(1.6rem, 2.5vw, 2.1rem)' }}
                  >
                    {stat.value}
                  </span>
                  <span className="text-white/38 text-[9px] font-medium tracking-[0.18em] uppercase leading-snug">
                    {stat.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function formatHeadline(headline: string) {
  const dotIdx = headline.lastIndexOf('.');
  if (dotIdx > 0 && dotIdx < headline.length - 1) {
    const first = headline.slice(0, dotIdx + 1);
    const rest = headline.slice(dotIdx + 1).trim();
    return (
      <>
        {first}
        <br />
        <em style={{ fontStyle: 'italic', fontWeight: 400 }}>{rest}</em>
      </>
    );
  }
  // fallback: italicize last word
  const words = headline.split(' ');
  if (words.length > 3) {
    const last = words.slice(-2).join(' ');
    const rest = words.slice(0, -2).join(' ');
    return (
      <>
        {rest}{' '}
        <em style={{ fontStyle: 'italic', fontWeight: 400 }}>{last}</em>
      </>
    );
  }
  return headline;
}
